import { useAppTranslation } from '../i18n/useAppTranslation';
import { Box, Paper, Typography, Chip, Stack } from '@mui/material';
import { TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';

/**
 * « Progression depuis le début de saison » : pour chaque indicateur, compare
 * la 1re evaluation (debut) a la derniere (now), sous forme de barre horizontale
 * (valeur actuelle + marqueur du point de depart) et d'un pourcentage d'evolution
 * avec une fleche. Complete le graphique « Derniere seance vs Moyenne ».
 */
const INDICATORS: { key: string; label: string; color: string }[] = [
  { key: 'endurance', label: 'Endurance', color: '#4caf50' },
  { key: 'technical_mastery', label: 'Maîtrise technique', color: '#2196f3' },
  { key: 'mental_strength', label: 'Force mentale', color: '#8b5cf6' },
  { key: 'motivation', label: 'Motivation', color: '#9c27b0' },
  { key: 'explosivite', label: 'Explosivité', color: '#06b6d4' },
  { key: 'tactique', label: 'Tactique', color: '#f59e0b' },
  { key: 'volontaire', label: 'Volonté', color: '#84cc16' },
  { key: 'fatigue_level', label: 'En forme', color: '#795548' },
  { key: 'stress_level', label: 'Gestion émotion', color: '#f44336' },
  { key: 'coordination', label: 'Coordination', color: '#0288d1' },
];

type Props = { debut: any; now: any };

const fmt = (v: number) => v.toFixed(1).replace('.', ',');

export default function SeasonProgress({ debut, now }: Props) {
  const { tr } = useAppTranslation();
  if (!debut || !now) return null;

  return (
    <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 3 }, borderRadius: 4 }}>
      <Typography variant="h6" fontWeight={700}>{tr("Progression depuis le début de saison")}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
         {tr("Début de saison → Aujourd'hui")} </Typography>

      <Stack spacing={1.75}>
        {INDICATORS.map(({ key, label, color }) => {
          const d = Number(debut[key]) || 0;
          const n = Number(now[key]) || 0;
          const pct = d > 0 ? Math.round(((n - d) / d) * 100) : (n > 0 ? 100 : 0);
          const up = n > d;
          const down = n < d;
          const trend = up ? '#16a34a' : down ? '#dc2626' : '#6b7280';
          const Icon = up ? TrendingUp : down ? TrendingDown : TrendingFlat;
          return (
            <Box key={key}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{tr(label)}</Typography>
                <Chip
                  size="small"
                  icon={<Icon sx={{ fontSize: 16 }} />}
                  label={`${pct > 0 ? '+' : ''}${pct} %`}
                  sx={{
                    bgcolor: `${trend}22`,
                    color: trend,
                    fontWeight: 800,
                    '& .MuiChip-icon': { color: trend },
                  }}
                />
              </Box>

              {/* Barre : remplissage = valeur actuelle ; trait = valeur de depart */}
              <Box sx={{ position: 'relative', height: 12, borderRadius: 6, bgcolor: 'action.hover', overflow: 'visible' }}>
                <Box sx={{
                  position: 'absolute', left: 0, top: 0, bottom: 0,
                  width: `${(n / 10) * 100}%`, bgcolor: color, borderRadius: 6,
                  transition: 'width .5s ease',
                }} />
                <Box sx={{
                  position: 'absolute', left: `calc(${(d / 10) * 100}% - 1px)`, top: -3, bottom: -3,
                  width: 2, bgcolor: 'text.primary', opacity: 0.55, borderRadius: 1,
                }} />
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.4 }}>
                <Typography variant="caption" color="text.secondary">{tr("Début :")} {fmt(d)}/10</Typography>
                <Typography variant="caption" sx={{ fontWeight: 700, color }}>{tr("Aujourd'hui :")} {fmt(n)}/10</Typography>
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
}
